import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './Category.css';
import { useNavigate, useParams } from 'react-router-dom';
import ChevronRight from '../../icons/ChevronRight';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';

function Category() {
    const { categoryName, subCategoryName } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const langPrefix = useLanguagePrefix();

    const [subcategories, setSubcategories] = useState([]);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    const formattedCategoryName = categoryName?.replace(/-/g, ' ');
    const formattedSubCategoryName = subCategoryName?.replace(/-/g, ' ');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await fetch(`https://api.prostay.com/WebsiteKnowledgebase/get-articles/${formattedCategoryName}`);
                if (!response.ok) throw new Error('Failed to fetch');

                const data = await response.json();
                setSubcategories(data.subcategories);

                let matchedSub = data.subcategories.find(
                    sub => sub.subcategoryName.toLowerCase() === formattedSubCategoryName?.toLowerCase()
                );

                if (!matchedSub && data.subcategories.length > 0) {
                    // Set default to first if no match and no slug
                    matchedSub = data.subcategories[0];
                    // const fallbackSubSlug = matchedSub.subcategoryName.toLowerCase().replace(/\s+/g, '-');
                    //  navigate(`/resources/${categoryName}/${fallbackSubSlug}`, { replace: true });
                }

                setSelectedSubcategory(matchedSub);
            } catch (err) {
                console.error('Error loading category data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [formattedCategoryName, formattedSubCategoryName]);

    const getPreview = (htmlContent) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        let plainText = tempDiv.textContent || tempDiv.innerText || '';
        plainText = plainText.replace(/\[Fig_\d+\]/g, '');
        return plainText.length > 150 ? plainText.substring(0, 200).trim() + '...' : plainText.trim();
    };

    const generateSlug = (title) => {
        const slug = title
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    
        return slug ? `${slug}/` : '';
    };

    const capitalizeWords = (str) => str?.replace(/\b\w/g, (char) => char.toUpperCase()) || '';

    const getFilteredArticles = () => {
        if (!selectedSubcategory?.knowledgebaseArticles) return [];
        return selectedSubcategory.knowledgebaseArticles.filter(article =>
            article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            getPreview(article.content).toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const metaTitle = selectedSubcategory?.metaTitle || `Prostay – ${capitalizeWords(formattedCategoryName)}`;
    const metaDescription = selectedSubcategory?.metaDescription || `Explore resources about ${capitalizeWords(formattedCategoryName)}, tips, guides, and insights.`;
    const canonicalUrl = `https://www.prostay.com${langPrefix}/resources/${categoryName}${subCategoryName ? `/${subCategoryName}/` : '/'}`;

    return (
        <div>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={canonicalUrl} />

                <link rel="alternate" href={"https://www.prostay.com/en/" + categoryName + subCategoryName ? `/${subCategoryName}/` : '/'} />
                <link rel="alternate" href={"https://www.prostay.com/es/" + categoryName + subCategoryName ? `/${subCategoryName}/` : '/'} />

                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription}/>
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />
            </Helmet>

            <div className="category">
                <div className="background-image">
                    <div className="background-image-start">
                        <div className="d-flex align-items-center gap-2 gap-md-3 breadcrumbs">
                            <a href={`${langPrefix}/`} style={{ textDecoration: 'none' }}><small>{t('Home')}</small></a>
                            <ChevronRight />
                            <a href={`${langPrefix}/resources/`} style={{ textDecoration: 'none' }}><small>{t('Resources')}</small></a>
                            <ChevronRight />
                            <small style={{ color: "#1643E0" }}>{capitalizeWords(formattedCategoryName)}</small>
                        </div>
                        <div className="mt-4" style={{ maxWidth: '600px' }}>
                            <h2>{capitalizeWords(formattedCategoryName)}</h2>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="d-flex align-items-center justify-content-center mt-5">
                        <div className="spinner-container">
                            <div className="custom-spinner"></div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="search-bar-align-end mt-4 mt-md-5">
                            <div className="search-bar">
                                <input
                                    className="w-100"
                                    type="text"
                                    placeholder={t("Search")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 19L14.7 14.7M17 9C17 13.4183 13.4183 17 9 17C4.58172 17 1 13.4183 1 9C1 4.58172 4.58172 1 9 1C13.4183 1 17 4.58172 17 9Z" stroke="#7784A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        <div className="d-flex justify-content-center">
                            <div className="content-grid page-content mt-2 mt-md-3">
                                <div className="col">
                                    <div className="white-content-card">
                                        <h5>{t('Categories')}</h5>
                                        <hr />
                                        {subcategories.map(sub => (
                                            <a
                                                style={{ textDecoration: 'none' }}
                                                key={sub.subcategoryName}
                                                href={`/resources/${categoryName}/${generateSlug(sub.subcategoryName || '')}`}
                                                className={`subcategory-link ${sub.subcategoryName === selectedSubcategory?.subcategoryName ? 'active' : ''}`}
                                            >
                                                {sub.subcategoryName}
                                            </a>
                                        ))}
                                    </div>

                                    <div className="contact-card d-flex flex-column">
                                        <div>
                                            <h3 style={{ color: '#FFFFFF', fontSize: '20px' }}>{t('Contact support')}</h3>
                                            <p className="body-small" style={{ color: '#FFFFFF' }}>{t('24/7 help from our support team')}</p>
                                        </div>
                                        <a href={`${langPrefix}/contact-us/`} style={{ textDecoration: 'none' }}>
                                            <button className="light-blue-filled-btn-small" style={{ backgroundColor: "#22242A", color: "#fff", width: "fit-content" }}>
                                                <svg width="16" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 4L8.89 9.26C9.22 9.48 9.6 9.6 10 9.6C10.4 9.6 10.78 9.48 11.11 9.26L19 4M3 15H17C17.53 15 18.04 14.79 18.41 14.41C18.79 14.04 19 13.53 19 13V3C19 2.47 18.79 1.96 18.41 1.59C18.04 1.21 17.53 1 17 1H3C2.47 1 1.96 1.21 1.59 1.59C1.21 1.96 1 2.47 1 3V13C1 13.53 1.21 14.04 1.59 14.41C1.96 14.79 2.47 15 3 15Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {t('Get in Touch')}
                                            </button>
                                        </a>
                                    </div>
                                </div>

                                <div className="white-content-card">
                                    {selectedArticle ? (
                                        <div className="article-content" dangerouslySetInnerHTML={{ __html: selectedArticle?.content }} />
                                    ) : (
                                        <>
                                            <h4>{selectedSubcategory?.subcategoryName}</h4>
                                            <div className="d-flex flex-column mt-2 mt-md-4">
                                                {getFilteredArticles().map(article => (
                                                    <a
                                                        style={{ textDecoration: 'none' }}
                                                        href={`/resources/${categoryName}/${subCategoryName}/${generateSlug(article.title || '')}`}
                                                        key={article.id}
                                                        className="d-flex flex-column article"
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.6, marginBottom: 3 }}>
                                                                <circle cx="10" cy="10" r="8" fill="#1643E0" />
                                                            </svg>
                                                            <h5 style={{ margin: 0 }}>{article.title}</h5>
                                                        </div>
                                                        <p className="body-small">{getPreview(article.content)}</p>
                                                    </a>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default Category;
